import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

/**
 * Shared styles for ItemPage sections to ensure consistent design across all tabs
 */
export const useSharedStyles = makeStyles({
  card: {
    ...shorthands.margin("auto"),
    width: "100%",
    maxWidth: "1024px",
    borderTop: "4px solid rgb(0, 96, 168)",
    marginTop: "20px",
    marginBottom: "20px",
    ...shorthands.padding("0", "16px"),
    "@media (min-width: 480px)": {
      ...shorthands.padding("0", "20px"),
    },
    "@media (min-width: 768px)": {
      ...shorthands.padding("0", "24px"),
    },
  },
  section: {
    marginBottom: "20px",
    padding: "15px",
    borderRadius: "4px",
  },
  sectionTitle: {
    gridColumn: "1 / -1",
    fontWeight: "bold",
    marginTop: "20px",
    marginBottom: "10px",
    fontSize: "16px",
    color: "rgb(0, 96, 168)",
    borderBottom: "1px solid rgb(200, 220, 240)",
    paddingBottom: "5px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: "15px",
    alignItems: "start",
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    // Make labels less prominent
    "& label": {
      fontSize: "13px",
      color: tokens.colorNeutralForeground3,
      fontWeight: "normal",
    },
    // Make input fields more prominent
    "& input": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      color: tokens.colorNeutralForeground1,
      paddingBottom: "8px",
      backgroundColor: "transparent",
      boxShadow: "none !important",
      "&:focus": {
        border: "none !important",
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        outline: "none !important",
        boxShadow: "none !important",
      },
    },
    // Make select/dropdown elements more prominent
    "& select": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      color: tokens.colorNeutralForeground1,
      paddingBottom: "8px",
      backgroundColor: "transparent",
      boxShadow: "none !important",
      "&:focus": {
        border: "none !important",
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        outline: "none !important",
        boxShadow: "none !important",
      },
    },
    // More aggressive reset for all form elements
    "& *": {
      boxSizing: "border-box",
    },
    "& .fui-Input": {
      border: "none !important",
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      backgroundColor: "transparent",
      boxShadow: "none !important",
      "&::after, &::before": {
        border: "none !important",
        boxShadow: "none !important",
      },
      "&:focus-within": {
        boxShadow: "none !important",
      },
    },
    "& .fui-Dropdown": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      minHeight: "32px",
      backgroundColor: "transparent",
      boxShadow: "none !important",
      "&::after, &::before": {
        border: "none !important",
        boxShadow: "none !important",
      },
      "& .fui-Dropdown__button": {
        border: "none !important",
        fontSize: "15px",
        fontWeight: "500",
        color: tokens.colorNeutralForeground1,
        paddingBottom: "8px",
        backgroundColor: "transparent",
        boxShadow: "none !important",
      },
      "&:focus-within": {
        border: "none !important",
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        boxShadow: "none !important",
      },
    },
    "& .fui-Select": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      backgroundColor: "transparent",
      boxShadow: "none !important",
      "&::after, &::before": {
        border: "none !important",
        boxShadow: "none !important",
      },
      "& select": {
        border: "none !important",
        fontSize: "15px",
        fontWeight: "500",
        color: tokens.colorNeutralForeground1,
        backgroundColor: "transparent",
        boxShadow: "none !important",
      },
      "&:focus-within": {
        border: "none !important",
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        boxShadow: "none !important",
      },
    },
    // Target Fluent UI DatePicker component wrapper
    "& .fui-DatePicker": {
      border: "none !important",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: "0 !important",
      fontSize: "15px",
      fontWeight: "500",
      backgroundColor: "transparent",
      "& input": {
        border: "none !important",
        borderRadius: "0 !important",
        fontSize: "15px",
        fontWeight: "500",
        backgroundColor: "transparent",
      },
      "&:focus-within": {
        borderBottom: `1px solid ${tokens.colorBrandStroke1} !important`,
        boxShadow: "none",
      },
    },
    // Target field containers to prevent spacing issues
    "& .fui-Field": {
      "& > div": {
        minHeight: "auto",
      },
    },
  },
  numberInput: {
    "& input": {
      textAlign: "right",
    },
    // Align suffix/contentAfter with input text
    "& .fui-Input__contentAfter": {
      alignSelf: "flex-end",
      paddingBottom: "11px", // Match the input's paddingBottom + border
      fontSize: "15px", // Match the input's fontSize
      fontWeight: "500", // Match the input's fontWeight
      color: tokens.colorNeutralForeground2,
    },
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "20px",
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    "& .fui-RadioGroup": {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    "& .fui-Radio": {
      fontSize: "15px",
      fontWeight: "500",
    },
  },
  checkboxField: {
    "& .fui-Checkbox": {
      fontSize: "15px",
      fontWeight: "500",
    },
  },
});

/**
 * Helper functions for number formatting (Czech style with space as thousands separator)
 */
export const formatNumberCzech = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");
  // Add space as thousand separator
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const parseFormattedNumber = (value: string): string => {
  // Remove spaces to get the actual number
  return value.replace(/\s/g, "");
};
